import { Component, Input, OnInit, ViewChild } from '@angular/core';
import type { AxisRenderer, CategoryAxis, DateAxis, IColumnSeriesSettings } from '@amcharts/amcharts5/xy';
import { LocalizationService } from '../../services/localization.service';
import { PgChartComponent, PgChartCreateChart } from '../pg-chart/pg-chart.component';

export class PgChartBarsSeries {
    field: string
    name: string
    subtitle?: string
    color?: string|number
}

export interface PgChartBarsData {
    category?: string
    date?: number;
    _capped?: boolean;
    _actualTotal?: number;
}

@Component({
    selector: 'app-pg-chart-bars',
    templateUrl: './pg-chart-bars.component.html',
    styleUrls: ['./pg-chart-bars.component.scss'],
    standalone: false
})
export class PgChartBarsComponent implements OnInit {

    @Input() series:Array<PgChartBarsSeries>;
    @Input() data:Array<PgChartBarsData>;
    @Input() stacked = true;
    @Input() showLegend = true;
    /** Optional hard max for the Y axis (e.g. after capping spikes). */
    @Input() yMax:number = null;

    @ViewChild(PgChartComponent) chart: PgChartComponent;

    constructor(private localizationService:LocalizationService) { }

    ngOnInit(): void {
        
    }

    exportPng(): Promise<string | null> {
        if (this.chart == null) {
            return Promise.resolve(null);
        }
        return this.chart.exportPng();
    }

    private _formatShortDay(millis: number): string {
        const d = new Date(millis);
        const monthNames = this.localizationService.data?.monthNames;
        const month = monthNames?.[d.getMonth()]?.substr(0, 3)?.toLowerCase() || '';
        return d.getDate() + (month ? ' ' + month : '');
    }

    createChart:PgChartCreateChart = (root, am5, am5xy) => {
        if(this.data != null && this.series != null) {
            let chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panY: false,
                    layout: root.verticalLayout
                })
            );
    
            // Create Y-axis
            let yAxisSettings: any = {
                renderer: am5xy.AxisRendererY.new(root, {}),
                min: 0,
            };
            if(this.yMax != null && this.yMax > 0) {
                yAxisSettings.max = this.yMax;
                yAxisSettings.strictMinMax = true;
            }

            let yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, yAxisSettings)
            );

            let _isDate = true;
            for(let _item of this.data) {
                if(_item.date == null) {
                    _isDate = false;
                    break;
                }
            }

            let xAxis:CategoryAxis<AxisRenderer>|DateAxis<AxisRenderer> = null;

            if(_isDate) {
                xAxis = chart.xAxes.push(
                    am5xy.DateAxis.new(root, {
                        baseInterval: { timeUnit: 'day', count: 1 },
                        renderer: am5xy.AxisRendererX.new(root, {})
                    })
                );

                xAxis.get('renderer').labels.template.adapters.add('text', (text, target) => {
                    const dataItem: any = target.dataItem;
                    const value = dataItem?.get?.('value');
                    if(value != null) {
                        return this._formatShortDay(value);
                    }
                    return text;
                });
            }
            else {
                let xRenderer = am5xy.AxisRendererX.new(root, { 
                    minGridDistance: 30, 
                    minorGridEnabled: true
                });
                
                xRenderer.labels.template.setAll({
                    oversizedBehavior: 'truncate',
                    maxWidth: 150,
                    maxHeight: 150,
                    rotation: -45,
                    centerY: am5.p50,
                    centerX: am5.p100
                });
    
                // Create X-Axis
                xAxis = chart.xAxes.push(
                    am5xy.CategoryAxis.new(root, {
                        categoryField: 'category',
                        renderer: xRenderer,
                        tooltip: am5.Tooltip.new(root, {})
                    })
                );
            }

            xAxis.data.setAll(this.data);

            // NB: gli step in tutto sono 20

            let _step = Math.min(4, Math.max(1, Math.floor(20 / Math.max(1, this.series.length))));

            chart.get('colors').setAll({ step: _step })

            const dateLabel = (dataItem: any) => {
                if(_isDate) {
                    const value = dataItem?.get?.('valueX') ?? dataItem?.dataContext?.date;
                    if(value != null) return this._formatShortDay(value);
                }
                return dataItem?.dataContext?.category || '';
            };

            for(let i = 0; i < this.series.length; i++) {
                let _series = this.series[i];

                let _seriesName = _series.name;
                if(_series.subtitle) {
                    _seriesName = _series.name + '\n[fontSize: 14px]' + _series.subtitle + '[/]'
                }

                let _seriesSettings:IColumnSeriesSettings = {
                    name: _seriesName,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: _series.field,
                    valueXField: _isDate ? 'date': null,
                    categoryXField: _isDate ? null: 'category',
                    stacked: this.stacked
                }

                if(_series.color != null) {
                    _seriesSettings.fill = am5.color(_series.color);
                    _seriesSettings.stroke = am5.color(_series.color);
                }

                // Create series
                let series = chart.series.push(
                    am5xy.ColumnSeries.new(root, _seriesSettings)
                );

                series.columns.template.adapters.add('tooltipText', (text, target) => {
                    const ctx: any = target.dataItem?.dataContext;
                    const label = dateLabel(target.dataItem);
                    const seriesName = _series.name;
                    const displayValue = ctx?.[_series.field];
                    const actual = ctx?.['_' + _series.field + '_actual'];
                    const value = actual != null ? actual : displayValue;
                    let tip = '[bold]' + label + '[/] ' + seriesName + ': ' + value;
                    if(ctx?._capped && ctx?._actualTotal != null) {
                        tip += '\n(picco ' + ctx._actualTotal + ', barra tagliata)';
                    }
                    return tip;
                });

                series.columns.template.adapters.add('fillOpacity', (opacity, target) => {
                    const ctx: any = target.dataItem?.dataContext;
                    if(ctx?._capped) return 0.55;
                    return opacity ?? 1;
                });

                series.data.setAll(this.data);
            }
    
            if(this.showLegend) {
                // Add legend
                let legend = chart.children.push(am5.Legend.new(root, {
                    paddingTop: 10,
                    paddingBottom: 15
                }));
                legend.labels.template.setAll({
                    oversizedBehavior: 'wrap',
                    textAlign: 'left'
                });
                legend.data.setAll(chart.series.values);
            }
    
            // Add cursor
            chart.set('cursor', am5xy.XYCursor.new(root, {}));
        }
    }
}
